import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Home, LogOut, User, Sun, Moon, Newspaper, MessageSquare, Users, BookOpen, Calendar, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setIsMenuOpen(false);
  };

  const isMember = user && ['member', 'admin', 'superadmin'].includes(user.role);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b-2 border-purple-500/30">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 md:space-x-3 group min-w-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_roleplay-hub-30/artifacts/5y3hduhe_JRI%20LOGO.jpg" 
              alt="Just Rôle It" 
              className="h-10 w-10 md:h-14 md:w-14 object-contain transition-transform duration-300 group-hover:scale-110 drop-shadow-[0_0_15px_rgba(147,51,234,0.8)] flex-shrink-0"
            />
            <span className="text-sm md:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-purple-300 to-purple-500 drop-shadow-[0_0_10px_rgba(147,51,234,0.6)] tracking-wider truncate">
              JUST RÔLE IT
            </span>
          </Link>

          {/* Desktop Navigation - Simplified */}
          <nav className="hidden md:flex items-center space-x-1">
            {/* Actualités - visible pour tous */}
            <Link to="/news">
              <Button variant="ghost" className="text-purple-300 hover:text-purple-100 hover:bg-purple-900/30 neon-hover">
                <Newspaper className="mr-2 h-4 w-4" />
                Actualités
              </Button>
            </Link>
            
            {/* Jeux - visible pour tous */}
            <Link to="/games">
              <Button variant="ghost" className="text-purple-300 hover:text-purple-100 hover:bg-purple-900/30 neon-hover">
                <BookOpen className="mr-2 h-4 w-4" />
                Ludothèque
              </Button>
            </Link>
            
            {/* Calendrier - visible pour tous */}
            <Link to="/calendar">
              <Button variant="ghost" className="text-purple-300 hover:text-purple-100 hover:bg-purple-900/30 neon-hover">
                <Calendar className="mr-2 h-4 w-4" />
                Calendrier
              </Button>
            </Link>
            
            {/* FAQ - visible pour tous */}
            <Link to="/faq">
              <Button variant="ghost" className="text-purple-300 hover:text-purple-100 hover:bg-purple-900/30 neon-hover">
                <HelpCircle className="mr-2 h-4 w-4" />
                FAQ
              </Button>
            </Link>
            
            {isMember && (
              <Link to="/messages">
                <Button variant="ghost" className="text-purple-300 hover:text-purple-100 hover:bg-purple-900/30 neon-hover">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Messages
                </Button>
              </Link>
            )}
            
            {isMember && (
              <Link to="/dashboard">
                <Button variant="ghost" className="text-purple-300 hover:text-purple-100 hover:bg-purple-900/30 neon-hover">
                  <Users className="mr-2 h-4 w-4" />
                  Espace Membre
                </Button>
              </Link>
            )}
          </nav>

          {/* Right Side - Notifications, Theme Toggle & Auth */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Notifications - only for logged in members */}
            {isMember && <NotificationBell />}
            
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-purple-300 hover:text-purple-100 hover:bg-purple-900/30"
              title={isDark ? 'Mode clair' : 'Mode sombre'}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {user ? (
              <div className="flex items-center space-x-3">
                <Link to="/profile" className="text-purple-300 hover:text-purple-100 transition-colors">
                  <span className="text-sm">{user.name}</span>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-900/30 hover:text-purple-300"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Déconnexion
                </Button>
              </div>
            ) : (
              <Link to="/login">
                <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button">
                  Connexion
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Notifications Mobile */}
            {isMember && <NotificationBell />}
            
            {/* Theme Toggle Mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-purple-300"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-purple-300"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-purple-500/30">
            <nav className="flex flex-col space-y-2">
              {/* Actualités - visible pour tous */}
              <Link to="/news" onClick={() => setIsMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-purple-300 hover:text-purple-100 hover:bg-purple-900/30">
                  <Newspaper className="mr-2 h-4 w-4" />
                  Actualités
                </Button>
              </Link>
              
              {/* Ludothèque - visible pour tous */}
              <Link to="/games" onClick={() => setIsMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-purple-300 hover:text-purple-100 hover:bg-purple-900/30">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Ludothèque
                </Button>
              </Link>
              
              {/* Calendrier - visible pour tous */}
              <Link to="/calendar" onClick={() => setIsMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-purple-300 hover:text-purple-100 hover:bg-purple-900/30">
                  <Calendar className="mr-2 h-4 w-4" />
                  Calendrier
                </Button>
              </Link>
              
              {/* FAQ - visible pour tous */}
              <Link to="/faq" onClick={() => setIsMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-purple-300 hover:text-purple-100 hover:bg-purple-900/30">
                  <HelpCircle className="mr-2 h-4 w-4" />
                  FAQ
                </Button>
              </Link>
              
              {isMember && (
                <Link to="/messages" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-purple-300 hover:text-purple-100 hover:bg-purple-900/30">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Messages
                  </Button>
                </Link>
              )}
              
              {isMember && (
                <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-purple-300 hover:text-purple-100 hover:bg-purple-900/30">
                    <Users className="mr-2 h-4 w-4" />
                    Espace Membre
                  </Button>
                </Link>
              )}

              <div className="pt-4 border-t border-purple-500/30">
                {user ? (
                  <div className="space-y-2">
                    <Link to="/profile" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-purple-300 hover:text-purple-100 hover:bg-purple-900/30">
                        <User className="mr-2 h-4 w-4" />
                        Mon Profil ({user.name})
                      </Button>
                    </Link>
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      className="w-full border-purple-500 text-purple-400 hover:bg-purple-900/30"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Déconnexion
                    </Button>
                  </div>
                ) : (
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400">
                      Connexion
                    </Button>
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
